import { useMemo, useState } from 'react';
import { ArrowRight, Pencil, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { CellEditor } from './CellEditor';
import { EntityTypeBadge, StatusBadge } from './ImportBadges';
import {
  ID_COLUMN,
  ImportFile,
  ImportRow,
  RowResult,
  cellValue,
  editorKindOf,
} from './importModel';

const PAGE_SIZE = 100;

export type EditingCell = { rowNumber: number; column: string } | null;

type ImportTableProps = {
  file: ImportFile;
  /** The file as it was when `results` were computed. */
  resultFile: ImportFile;
  rows: ImportRow[];
  results: Map<number, RowResult>;
  /** The importable columns the file has, in template order. */
  importedColumns: string[];
  countries: string[];
  /** Read-only: no editing and no removing (the apply result). */
  readOnly?: boolean;
  validating?: boolean;
  editing?: EditingCell;
  onEditingChange?: (cell: EditingCell) => void;
  onCommit?: (rowNumber: number, column: string, value: string) => void;
  onRemove?: (rowNumber: number) => void;
};

export function ImportTable(props: ImportTableProps) {
  const { rows, importedColumns, readOnly } = props;
  const [page, setPage] = useState(0);
  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount - 1);
  const visibleRows = rows.slice(
    currentPage * PAGE_SIZE,
    (currentPage + 1) * PAGE_SIZE,
  );
  const columns = [ID_COLUMN, ...importedColumns];
  const resultRows = useMemo(
    () => new Map(props.resultFile.rows.map((r) => [r.rowNumber, r])),
    [props.resultFile],
  );

  return (
    <div className="tw:flex tw:flex-col tw:gap-2">
      <div className="tw:overflow-x-auto tw:rounded-md tw:border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="tw:w-12">Row</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>{ID_COLUMN}</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Name</TableHead>
              {importedColumns.map((column) => (
                <TableHead key={column} className="tw:font-mono tw:text-xs">
                  {column}
                </TableHead>
              ))}
              {!readOnly && <TableHead className="tw:w-10" />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibleRows.map((row) => (
              <ImportTableRow
                key={row.rowNumber}
                {...props}
                row={row}
                columns={columns}
                resultRow={resultRows.get(row.rowNumber)}
              />
            ))}
          </TableBody>
        </Table>
      </div>
      {pageCount > 1 && (
        <div className="tw:flex tw:items-center tw:justify-end tw:gap-2 tw:text-sm">
          <span className="tw:text-muted-foreground">
            Rows {currentPage * PAGE_SIZE + 1}–
            {Math.min(rows.length, (currentPage + 1) * PAGE_SIZE)} of{' '}
            {rows.length}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={currentPage === 0}
            onClick={() => setPage(currentPage - 1)}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={currentPage >= pageCount - 1}
            onClick={() => setPage(currentPage + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}

function ImportTableRow({
  file,
  resultFile,
  row,
  columns,
  resultRow,
  results,
  countries,
  readOnly,
  validating,
  editing,
  onEditingChange,
  onCommit,
  onRemove,
}: ImportTableProps & {
  row: ImportRow;
  columns: string[];
  resultRow: ImportRow | undefined;
}) {
  const result = results.get(row.rowNumber);
  const isPending = (column: string) =>
    !resultRow ||
    cellValue(file, row, column) !== cellValue(resultFile, resultRow, column);
  const rowPending = columns.some(isPending);
  const rowErrors = (result?.errors ?? []).filter((error) => !error.column);

  return (
    <>
      <TableRow
        data-row-number={row.rowNumber}
        className={cn(rowPending && 'tw:opacity-80')}
      >
        <TableCell className="tw:text-muted-foreground tw:tabular-nums">
          {row.rowNumber}
        </TableCell>
        <TableCell>
          {rowPending ? (
            <span className="tw:text-muted-foreground tw:inline-flex tw:items-center tw:gap-1 tw:text-xs">
              {validating && <Spinner className="tw:size-3" />}
              pending
            </span>
          ) : (
            <StatusBadge status={result?.status} />
          )}
        </TableCell>
        <ImportCell
          column={ID_COLUMN}
          value={cellValue(file, row, ID_COLUMN)}
          result={result}
          pending={isPending(ID_COLUMN)}
          countries={countries}
          readOnly={readOnly}
          isEditing={
            editing?.rowNumber === row.rowNumber && editing.column === ID_COLUMN
          }
          onStartEdit={() =>
            onEditingChange?.({ rowNumber: row.rowNumber, column: ID_COLUMN })
          }
          onCancel={() => onEditingChange?.(null)}
          onCommit={(value) => onCommit?.(row.rowNumber, ID_COLUMN, value)}
        />
        <TableCell>
          <EntityTypeBadge entityType={result?.entity_type} />
        </TableCell>
        <TableCell
          className="tw:max-w-48 tw:truncate"
          title={result?.name ?? ''}
        >
          {result?.name ?? <span className="tw:text-muted-foreground">–</span>}
        </TableCell>
        {columns.slice(1).map((column) => (
          <ImportCell
            key={column}
            column={column}
            value={cellValue(file, row, column)}
            result={result}
            pending={isPending(column)}
            countries={countries}
            readOnly={readOnly}
            isEditing={
              editing?.rowNumber === row.rowNumber && editing.column === column
            }
            onStartEdit={() =>
              onEditingChange?.({ rowNumber: row.rowNumber, column })
            }
            onCancel={() => onEditingChange?.(null)}
            onCommit={(value) => onCommit?.(row.rowNumber, column, value)}
          />
        ))}
        {!readOnly && (
          <TableCell>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={`Remove row ${row.rowNumber} from the import`}
                  onClick={() => onRemove?.(row.rowNumber)}
                >
                  <Trash2 />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Remove this row from the import</TooltipContent>
            </Tooltip>
          </TableCell>
        )}
      </TableRow>
      {rowErrors.length > 0 && !rowPending && (
        <TableRow className="tw:bg-destructive/5 tw:hover:bg-destructive/5">
          <TableCell />
          <TableCell
            colSpan={columns.length + 3 + (readOnly ? 0 : 1)}
            className="tw:text-destructive tw:text-sm tw:whitespace-normal"
          >
            {rowErrors.map((error) => error.message).join(' ')}
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

type ImportCellProps = {
  column: string;
  value: string;
  result: RowResult | undefined;
  pending: boolean;
  countries: string[];
  readOnly?: boolean;
  isEditing: boolean;
  onStartEdit: () => void;
  onCancel: () => void;
  onCommit: (value: string) => void;
};

function ImportCell({
  column,
  value,
  result,
  pending,
  countries,
  readOnly,
  isEditing,
  onStartEdit,
  onCancel,
  onCommit,
}: ImportCellProps) {
  if (isEditing && !readOnly) {
    return (
      <TableCell data-column={column} className="tw:align-top">
        <CellEditor
          kind={editorKindOf(column)}
          value={value}
          countries={countries}
          onCommit={onCommit}
          onCancel={onCancel}
        />
      </TableCell>
    );
  }

  // Server diffs and errors describe the validated value; hide them while the
  // cell waits for re-validation.
  const errors = pending
    ? []
    : (result?.errors ?? []).filter((error) => error.column === column);
  const change = pending
    ? undefined
    : result?.changes.find((c) => c.column === column);
  const hasError = errors.length > 0;

  const content = change ? (
    <span className="tw:inline-flex tw:items-center tw:gap-1">
      <span className="tw:text-muted-foreground tw:line-through">
        {change.old_value || <EmptyValue />}
      </span>
      <ArrowRight className="tw:text-muted-foreground tw:size-3 tw:shrink-0" />
      <span className="tw:font-medium tw:text-blue-800">
        {change.new_value || <EmptyValue />}
      </span>
    </span>
  ) : (
    <span className={cn(pending && 'tw:italic')}>
      {value || <EmptyValue />}
    </span>
  );

  const body = (
    <div className="tw:flex tw:flex-col tw:gap-0.5">
      <div className="tw:group tw:flex tw:items-center tw:gap-1">
        {content}
        {!readOnly && (
          <Pencil className="tw:text-muted-foreground tw:size-3 tw:shrink-0 tw:opacity-0 tw:group-hover:opacity-100" />
        )}
      </div>
      {errors.map((error) => (
        <span
          key={error.message}
          className="tw:text-destructive tw:max-w-56 tw:text-xs tw:whitespace-normal"
        >
          {error.message}
        </span>
      ))}
    </div>
  );

  return (
    <TableCell
      data-column={column}
      className={cn(
        'tw:align-top',
        hasError && 'tw:bg-destructive/10',
        change && 'tw:bg-blue-50',
        !readOnly && 'tw:cursor-pointer tw:hover:bg-muted/60',
      )}
      onClick={readOnly ? undefined : onStartEdit}
      title={hasError ? errors.map((e) => e.message).join('\n') : undefined}
    >
      {readOnly ? (
        body
      ) : (
        <button
          type="button"
          className="tw:w-full tw:cursor-pointer tw:text-left"
          aria-label={`Edit ${column} of row`}
        >
          {body}
        </button>
      )}
    </TableCell>
  );
}

function EmptyValue() {
  return <span className="tw:text-muted-foreground tw:italic">empty</span>;
}
